import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

const setHomeProductVideos = {
  // Propriedades do objeto
  homeVideosWrappers: [],
  homeVideosSwipers: [],
  activeVideo: null,

  // Inicializa os event listeners para os botões de play
  initVideoControls: function () {
    const { homeVideosWrappers, handleVideoPlay } = setHomeProductVideos;

    if (!homeVideosWrappers.length) return;

    homeVideosWrappers.forEach(wrapper => {
      const videoContainers = wrapper.querySelectorAll('[data-video-container]');
      videoContainers.forEach(container => {
        const playBtn = container.querySelector('.video-overlay');
        if (playBtn) {
          playBtn.addEventListener('click', (e) => handleVideoPlay(e, container));
        }
      });
    });
  },

  // Manipula o click no botão de play 
  handleVideoPlay: async function (event, container) {
    event.preventDefault();

    const { showVideoLoading, hideVideoLoading, loadVideo } = setHomeProductVideos;

    showVideoLoading(container);

    try {
      await loadVideo(container, true);
      setHomeProductVideos.activeVideo = container;
    } catch (error) {
      setTimeout(() => {
        hideVideoLoading(container);
      }, 3000);
    }
  },

  // Mostra o estado de loading no container do vídeo
  showVideoLoading: function (container) {
    const loading = container.querySelector('.video-loading');
    const overlay = container.querySelector('.video-overlay');
    if (loading) loading.style.display = 'flex';
    if (overlay) overlay.style.display = 'none';
  },

  // Esconde o estado de loading no container do vídeo
  hideVideoLoading: function (container) {
    const loading = container.querySelector('.video-loading');
    const overlay = container.querySelector('.video-overlay');
    if (loading) loading.style.display = 'none';
    if (overlay) overlay.style.display = 'flex';
  },

  // Mostra erro visual no container do vídeo
  showVideoError: function (container, errorMessage) {
    const loading = container.querySelector('.video-loading');
    if (loading) {
      loading.innerHTML = `
        <div class="error-message" style="
          color: white;
          text-align: center;
          font-size: 14px;
          padding: 20px;
        ">
          <div style="margin-bottom: 10px;">X</div>
          <div>Erro ao carregar vídeo</div>
          <div style="font-size: 12px; opacity: 0.8; margin-top: 5px;">
            ${errorMessage}
          </div>
        </div>
      `;
    }
  },

  // Carrega o iframe do vídeo substituindo a thumbnail
  loadVideo: function (container, isAutoplay = false) {
    return new Promise((resolve, reject) => {
      const videoUrl = container.dataset.videoUrl;
      const videoType = container.dataset.videoType;
      const videoId = container.dataset.videoId;
      const thumbnail = container.querySelector('.video-thumbnail');

      if (!videoUrl) {
        reject(new Error('URL do vídeo não encontrada'));
        return;
      }

      // Flag para evitar múltiplas execuções
      let isLoaded = false;

      // Adiciona parâmetros de autoplay na URL se necessário
      let finalVideoUrl = videoUrl;
      if (isAutoplay || container.dataset.autoplay === 'true') {
        if (videoType === 'youtube' && !finalVideoUrl.includes('autoplay=1')) {
          finalVideoUrl += (finalVideoUrl.includes('?') ? '&' : '?') + 'autoplay=1';
        } else if (videoType === 'vimeo' && !finalVideoUrl.includes('autoplay=1')) {
          finalVideoUrl += (finalVideoUrl.includes('?') ? '&' : '?') + 'autoplay=1';
        }
      }

      // Cria iframe
      const iframe = document.createElement('iframe');
      iframe.src = finalVideoUrl;
      iframe.frameBorder = '0';
      iframe.allow = 'autoplay; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';

      if (thumbnail) {
        iframe.setAttribute('aria-label', thumbnail.alt || 'Vídeo');
      }

      // Timeout aumentado para 15 segundos
      const timeout = setTimeout(() => {
        if (!isLoaded) {
          reject(new Error('Timeout ao carregar vídeo'));
        }
      }, 15000);

      // Função para finalizar carregamento (só executa uma vez)
      const finishLoading = () => {
        if (isLoaded) return;

        isLoaded = true;
        clearTimeout(timeout);

        const thumbnailEl = container.querySelector('.video-thumbnail');
        const loading = container.querySelector('.video-loading');
        const overlay = container.querySelector('.video-overlay');

        if (thumbnailEl) thumbnailEl.remove();
        if (loading) loading.remove();
        if (overlay) overlay.remove();

        if (!container.contains(iframe)) {
          container.appendChild(iframe);
        }
        container.classList.add('playing');
        resolve();
      };

      // Múltiplos eventos para garantir carregamento
      iframe.onload = finishLoading;
      iframe.addEventListener('load', finishLoading);

      // Aguarda um tempo mínimo antes de considerar carregado (fallback)
      setTimeout(() => {
        if (!isLoaded) finishLoading();
      }, 3000);

      iframe.onerror = (error) => {
        if (!isLoaded) {
          clearTimeout(timeout);
          reject(new Error('Erro ao carregar iframe'));
        }
      };

      container.appendChild(iframe);
    });
  },

  // Pausa um vídeo específico (remove iframe e restaura thumbnail)
  pauseVideo: function (container) {
    const { restoreVideoThumbnail } = setHomeProductVideos;
    if (!container) return;
    const iframe = container.querySelector('iframe');
    if (iframe) {
      iframe.remove();
      restoreVideoThumbnail(container);
    }
    container.classList.remove('playing');
    if (setHomeProductVideos.activeVideo === container) {
      setHomeProductVideos.activeVideo = null;
    }
  },

  // Pausa todos os vídeos ativos (exceto o atual)
  pauseAllVideos: function (currentContainer = null) {
    const { homeVideosWrappers, pauseVideo } = setHomeProductVideos;
    if (!homeVideosWrappers.length) return;
    homeVideosWrappers.forEach(wrapper => {
      const playingVideos = wrapper.querySelectorAll('[data-video-container].playing');
      playingVideos.forEach(container => {
        if (container !== currentContainer) {
          pauseVideo(container);
        }
      });
    });
    if (!currentContainer) {
      setHomeProductVideos.activeVideo = null;
    }
  },

  // Carrega todos os vídeos automaticamente para reprodução simultânea
  autoloadAllVideos: function () {
    const { homeVideosWrappers, loadVideo } = setHomeProductVideos;
    if (!homeVideosWrappers.length) return;

    homeVideosWrappers.forEach(wrapper => {
      const videoContainers = wrapper.querySelectorAll('[data-video-container]');
      videoContainers.forEach(container => {
        // Marca como autoplay e carrega o vídeo
        container.dataset.autoplay = 'true';
        loadVideo(container, true).catch(() => {
          // Falha silenciosa - vídeo pode não carregar automaticamente
        });
      });
    });
  },

  // Restaura a thumbnail original
  restoreVideoThumbnail: function (container) {
    const { handleVideoPlay } = setHomeProductVideos;
    const videoId = container.dataset.videoId;
    const videoType = container.dataset.videoType;
    const videoTitle = container.dataset.videoTitle || 'Vídeo';

    let thumbnailUrl;
    if (videoType === 'youtube') {
      thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    } else if (videoType === 'vimeo') {
      thumbnailUrl = `https://vumbnail.com/${videoId}.jpg`;
    }
    if (!thumbnailUrl) return;

    const thumbnail = document.createElement('img');
    thumbnail.className = 'video-thumbnail loaded';
    thumbnail.src = thumbnailUrl;
    thumbnail.alt = videoTitle;

    const overlay = document.createElement('div');
    overlay.className = 'video-overlay';

    const playBtn = document.createElement('button');
    playBtn.className = 'video-play-btn';
    playBtn.type = 'button';
    playBtn.setAttribute('aria-label', `Reproduzir ${videoTitle}`);
    playBtn.innerHTML = `
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <path d="M21.3812 32.832L32.3557 24.607C32.9902 24.137 32.9902 23.197 32.3557 22.727L21.3812 14.502C21.2066 14.3711 20.999 14.2913 20.7817 14.2717C20.5644 14.2521 20.3459 14.2935 20.1507 14.391C19.9555 14.4886 19.7914 14.6386 19.6767 14.8243C19.5619 15.0099 19.5012 15.2238 19.5012 15.442V31.892C19.5012 32.8555 20.6057 33.4195 21.3812 32.832ZM24.2012 0.166992C11.2292 0.166992 0.701172 10.695 0.701172 23.667C0.701172 36.639 11.2292 47.167 24.2012 47.167C37.1732 47.167 47.7012 36.639 47.7012 23.667C47.7012 10.695 37.1732 0.166992 24.2012 0.166992ZM24.2012 42.467C13.8377 42.467 5.40117 34.0305 5.40117 23.667C5.40117 13.3035 13.8377 4.86699 24.2012 4.86699C34.5647 4.86699 43.0012 13.3035 43.0012 23.667C43.0012 34.0305 34.5647 42.467 24.2012 42.467Z" fill="white"/>
      </svg>
    `;
    overlay.appendChild(playBtn);
    container.appendChild(thumbnail);
    container.appendChild(overlay);
    playBtn.addEventListener('click', (e) => handleVideoPlay(e, container));
  },

  // Observa se o vídeo ativo está visível na tela
  observeActiveVideoVisibility: function () {
    if (!window.IntersectionObserver || !setHomeProductVideos.homeVideosWrappers.length) return;
    const { pauseVideo } = setHomeProductVideos;

    setHomeProductVideos._videoVisibilityObservers?.forEach(obs => obs.disconnect());
    setHomeProductVideos._videoVisibilityObservers = [];

    setHomeProductVideos.homeVideosWrappers.forEach(wrapper => {
      const videoContainers = wrapper.querySelectorAll('[data-video-container]');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (!isIntersecting && target.classList.contains('playing')) {
            pauseVideo(target);
          }
        });
      }, { threshold: 0.1 });

      videoContainers.forEach(container => observer.observe(container));
      setHomeProductVideos._videoVisibilityObservers.push(observer);
    });
  },

  // Inicializa o módulo
  init: function () {
    const { initVideoControls, observeActiveVideoVisibility, autoloadAllVideos } = setHomeProductVideos;

    setHomeProductVideos.homeVideosWrappers = Array.from(document.querySelectorAll("[data-swiper-home-videos]"));
    setHomeProductVideos.homeVideosSwipers = [];

    if (!setHomeProductVideos.homeVideosWrappers.length) return;

    setHomeProductVideos.homeVideosWrappers.forEach(wrapper => {
      const videosSwiper = wrapper.querySelector('.swiper');
      if (videosSwiper) {
        const pagination = wrapper.querySelector('.swiper-pagination');
        const prevEl = wrapper.querySelector('.swiper-button-prev');
        const nextEl = wrapper.querySelector('.swiper-button-next');
        const swiperInstance = new Swiper(videosSwiper, {
          modules: [Navigation, Pagination],
          slidesPerView: 1.2,
          spaceBetween: 12,
          watchOverflow: true,
          preloadImages: false,
          centerInsufficientSlides: true,
          speed: 1000,
          autoplay: {
            delay: 5000,
            disableOnInteraction: false,
          },
          navigation: {
            prevEl: prevEl,
            nextEl: nextEl,
            disabledClass: '-disabled',
          },
          pagination: {
            el: pagination,
            type: 'bullets',
            clickable: true,
          },
          breakpoints: {
            530: {
              slidesPerView: 1.8,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 2.5,
            },
            992: {
              slidesPerView: 3,
              spaceBetween: 32,
            },
            1368: {
              slidesPerView: 5,
            }
          },
        });
        setHomeProductVideos.homeVideosSwipers.push(swiperInstance);
      }
    });

    initVideoControls();
    observeActiveVideoVisibility();
    autoloadAllVideos();
  },
}

export default setHomeProductVideos;
